import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Chrome, Shield } from "lucide-react";
import { NSBLogo } from "../components/NSBLogo";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// MLB Team email domains for scouts
const MLB_TEAM_DOMAINS = [
  // American League
  "orioles.com", "redsox.com", "whitesox.com", "cleguardians.com", "tigers.com",
  "astros.com", "royals.com", "angels.com", "twins.com", "yankees.com",
  "athletics.com", "mariners.com", "raysbaseball.com", "rangers.com", "bluejays.com",
  // National League
  "dbacks.com", "braves.com", "cubs.com", "reds.com", "rockies.com",
  "dodgers.com", "marlins.com", "brewers.com", "mets.com", "phillies.com",
  "pirates.com", "padres.com", "sfgiants.com", "cardinals.com", "nationals.com",
  // Generic MLB
  "mlb.com"
];

// Check if email domain is valid for scouts
const isValidScoutEmail = (email) => {
  const domain = email.toLowerCase().split('@')[1];
  return MLB_TEAM_DOMAINS.some(d => domain === d || domain?.endsWith(`.${d}`));
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "athlete",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    // .edu email validation for coaches
    if (formData.role === "coach" && !formData.email.toLowerCase().endsWith(".edu")) {
      toast.error("Coaches must register with a valid .edu email address");
      return;
    }

    // MLB team email validation for scouts
    if (formData.role === "scout" && !isValidScoutEmail(formData.email)) {
      toast.error("MLB Scouts must register with a valid team email (@team.com or @mlb.com)");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Registration failed");
      }

      localStorage.setItem("token", data.access_token);
      localStorage.setItem("user", JSON.stringify(data.user));
      
      toast.success("Welcome to NSB!");
      
      // Redirect athletes to profile creation, others to their dashboard
      if (data.user.role === "athlete") {
        navigate("/create-profile", { replace: true });
      } else {
        const roleRoutes = {
          coach: "/dashboard/coach",
          scout: "/dashboard/scout",
          agent: "/dashboard/agent",
          admin: "/dashboard/admin",
        };
        navigate(roleRoutes[data.user.role] || "/dashboard/coach", { replace: true });
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url(https://cdn.abacus.ai/images/bc2afa59-46af-4b8a-a1f3-3eaf3422e896.png)`,
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center">
            <NSBLogo className="h-14" light={true} />
          </Link>
          
          <div className="max-w-md">
            <h1 className="text-4xl font-black leading-tight mb-4">
              Start Your<br />
              <span className="text-blue-900-light">Journey Today</span>
            </h1>
            <p className="text-white/80 mb-8">
              Join thousands of athletes, coaches, and scouts on the nation's premier scouting platform.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm text-white/90">Verified athlete profiles</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm text-white/90">Professional scouting reports</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-sm text-white/90">Direct college coach connections</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8 text-sm text-white/60">
            <span>Free to join</span>
            <span>Instant access</span>
            <span>No credit card required</span>
          </div>
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 overflow-y-auto bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/" className="inline-flex items-center">
              <NSBLogo className="h-12" light={false} />
            </Link>
          </div>

          <div className="text-center lg:text-left mb-8">
            <h2 className="text-3xl font-bold text-nsb-navy">Create Account</h2>
            <p className="text-gray-500 mt-2">Join the NSB family today</p>
          </div>

          {/* Google Register */}
          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white border-2 border-gray-200 text-nsb-navy hover:bg-gray-50 flex items-center justify-center gap-3 py-6 mb-6"
            data-testid="google-register-btn"
          >
            <Chrome size={20} className="text-blue-900" />
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-4 text-gray-500 uppercase">Or register with email</span>
            </div>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm text-gray-600">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-gray-600">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={
                    formData.role === "coach" ? "you@school.edu" : 
                    formData.role === "scout" ? "you@team.com" : 
                    "you@example.com"
                  }
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900"
                  required
                  data-testid="register-email-input"
                />
              </div>
              {formData.role === "coach" && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Coaches must use a valid .edu school email address
                </p>
              )}
              {formData.role === "scout" && (
                <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  MLB Scouts must use a valid team email (@team.com or @mlb.com)
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm text-gray-600">I am a...</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="register-role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="athlete">Athlete / Player</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="scout">Scout / Evaluator</SelectItem>
                  <SelectItem value="agent">Agent / Collective</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-gray-600">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm text-gray-600">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 bg-gray-50 border-gray-200 focus:border-blue-900"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full btn-primary py-6 flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating Account..." : "Create Account"}
              {!loading && <ArrowRight size={18} />}
            </Button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            By creating an account, you agree to our{" "}
            <a href="#" className="text-blue-900 hover:underline">Terms of Service</a>{" "}
            and{" "}
            <a href="#" className="text-blue-900 hover:underline">Privacy Policy</a>
          </p>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-900 font-semibold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
